import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getBusinessList,
  getVehicleSubcategory,
} from "../../../../../apis/Business/Business";
import { getVehicleFuelSubcategory } from "../../../../../apis/Business/FuelVehicleApi";
import { getOtherVehicleSubcategory } from "../../../../../apis/Business/Business";
import { getIngoingTransportSubcategory } from "../../../../../apis/Business/IngoingTransportApi";
import { getOutgoingTransportSubcategory } from "../../../../../apis/Business/OutgoingTransportApi";
import { getCustomSubcategory } from "../../../../../apis/Business/InventoryApi";
import { getTravelSubcategory } from "../../../../../apis/Business/TravelApi";
import { getWasteSubcategory } from "../../../../../apis/Business/WasteApi";
import getDropdownOptions from "../SupplierTable/getDropdownOptions";
import { YearTabContext } from "../../../../../components/Skeleton";
import "./index.css";
import selectedCheck from "../../../../../assets/design-revamp-company/company-epr/selected-check-erp.svg";
import unselectedCheck from "../../../../../assets/design-revamp-company/company-epr/unselected-check-erp.svg";
import selectAllUnselected from "../../../../../assets/design-revamp-company/company-epr/select-all-unselected.svg";
import sortingIcon from "../../../../../assets/design-revamp/default-sort.svg";
import upArrow from "../../../../../assets/design-revamp-company/company-epr/up-arrow.svg";
import downArrow from "../../../../../assets/design-revamp-company/company-epr/down-arrow.svg";
import "../DnDTable/index.css";
import infoIcon from "../../../../../assets/design-revamp-company/company-epr/info-blue.svg";
import AllocateModal from "../QAModalInfo/AllocateModal";
import getCustomSubcategories from "./getDropdownSubcategory";

const Step2Table = ({
  continueBin,
  businessId,
  chosenYear,
  setSelectedRowsInStep2,
  selectedRowsInStep2,
  isView = false,
}) => {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const year = urlParams.get("year");
  const [mainData, setMainData] = useState(continueBin);
  const yearTab = useContext(YearTabContext);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [dropdownFacility, setDropdownFacility] = useState([]);
  const [dropdownCategory, setDropdownCategory] = useState([]);
  const [dropdownSubcategory, setDropdownSubcategory] = useState([]);
  const [dropdownEmission, setDropdownEmission] = useState([]);
  const [facilityId, setFacilityId] = useState();
  const [sortColumn, setSortColumn] = useState("account_number");
  const [sortDirection, setSortDirection] = useState("asc");
  const [parentRowData, setParentRowData] = useState({});
  const [subcategoryStates, setSubcategoryStates] = useState({});
  const [emissionFactorStates, setEmissionFactorStates] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const [subcategoryVehicle, setSubcategoryVehicle] = useState([]);
  const [subcategoryFuelVehicle, setSubcategoryFuelVehicle] = useState([]);
  const [subcategoryOtherVehicle, setSubcategoryOtherVehicle] = useState([]);
  const [subcategoryIngoingTransport, setSubcategoryIngoingTransport] =
    useState([]);
  const [subcategoryOutgoingTransport, setSubcategoryOutgoingTransport] =
    useState([]);
  const [subcategoryCustomInventory, setSubcategoryCustomInventory] = useState(
    []
  );
  const [subcategoryTravel, setSubcategoryTravel] = useState([]);
  const [subcategoryWaste, setSubcategoryWaste] = useState([]);

  const [categoriesWithSubcategories, setCategoriesWithSubcategories] =
    useState({
      Energy: [t("MAINS_INPUT"), t("HOME_INPUT"), t("SYMBIOTIC_INPUTS")],
      Water: [
        t("WATER_USAGE"),
        t("WATER_FOUND_LOCATION"),
        t("EQUIPMENT_LOCATION"),
        t("WATER_SYMBIOTIC_INPUT"),
      ],
      "Registered Vehicle": [],
      "Fuel Vehicle": [],
      "Other Vehicle": [],
      "Emission from gasses": [
        t("REFRIGERATION_INPUTS"),
        t("INDUSTRIAL_GAS_INPUTS"),
        t("SUPPRESSANT_INPUTS"),
      ],
      "Ingoing Transport": [],
      "Outgoing Transport": [],
      Inventory: [
        t("INV_VEHICLE"),
        t("MACHINERY"),
        t("INV_BUILDINGS"),
        t("PURCHASED_SERVICES"),
        t("INVENTORY_INGOING_PACKAGING"),
        t("INVENTORY_PURCHASED_PACKAGING"),
        t("CHEMICALS"),
      ],
      "Business Travel and Hotel Stays": [],
      "Employee Commute": [],
      Remote: [],
      "Surplus Resources": [
        t("TREATMENT_INGOING_PACKAGING"),
        t("SURPLUS_ENERGY"),
        t("WATER"),
      ],
      "Processing Sold Intermediate": [
        t("PROCESSING_NO_DATA_TITLE"),
        t("PROCESSING_DATA_TITLE"),
        t("WASTE_GEN_TITLE"),
      ],
    });

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      await getBusinessList({
        filterYear: year ? year : yearTab,
        setFacilities: setDropdownFacility,
      });

      const categories = Object.keys(categoriesWithSubcategories).map(
        (category, index) => ({
          id: index + 1,
          name: category,
          value: category,
          label: category,
        })
      );
      setDropdownCategory(categories);
    };

    fetchDropdownOptions();
  }, [chosenYear, yearTab, year, categoriesWithSubcategories]);

  // Group data by account number with independent parent row tracking
  const groupedData = useMemo(() => {
    const groups = mainData.reduce((acc, item) => {
      const key = item.account_number;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          account_number: key,
          account_name: item.account_name,
          amount: 0,
          entries: 0,
          uniqueNames: new Set(),
          originalIds: [],
          facility: parentRowData[key]?.facility || "",
          category: parentRowData[key]?.category || "",
          subcategory: parentRowData[key]?.subcategory || "",
          emission_factor: parentRowData[key]?.emission_factor || "",
        };
      }
      acc[key].amount += parseFloat(item.amount);
      acc[key].entries += 1;
      acc[key].uniqueNames.add(item.account_name);
      acc[key].originalIds.push(item.id);
      return acc;
    }, {});

    return Object.values(groups)
      .map((group) => ({
        ...group,
        uniqueNames: Array.from(group.uniqueNames),
      }))
      .sort((a, b) => {
        // Handle sorting based on the current sortColumn and sortDirection
        let valueA = a[sortColumn];
        let valueB = b[sortColumn];

        // Convert to string for consistent comparison
        const stringA = String(valueA ?? "").toLowerCase();
        const stringB = String(valueB ?? "").toLowerCase();

        // Numeric sorting for amount
        if (sortColumn === "amount") {
          return sortDirection === "asc"
            ? (parseFloat(valueA) || 0) - (parseFloat(valueB) || 0)
            : (parseFloat(valueB) || 0) - (parseFloat(valueA) || 0);
        }

        // String-based sorting for other columns
        if (stringA < stringB) return sortDirection === "asc" ? -1 : 1;
        if (stringA > stringB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [mainData, parentRowData, sortColumn, sortDirection]);

  // Handling initial data population and subcategory fetching
  useEffect(() => {
    const populateInitialDropdowns = async () => {
      // If there's data and facilities are loaded
      if (mainData.length > 0 && dropdownFacility.length > 0) {
        // Try to get the first facility if not already set
        if (!facilityId && dropdownFacility[0]) {
          const firstFacility = dropdownFacility[0];

          // Set facility for all rows
          const updatedMainData = mainData.map((item) => ({
            ...item,
            facility: item.facility.value
              ? item.facility.value
              : firstFacility.value,
            facility_id: item.facility_id ? item.facility_id : firstFacility.id,
          }));
          setMainData(updatedMainData);

          // Set facility ID and trigger subcategory fetching
          setFacilityId(firstFacility.id);

          // Fetch all subcategories for this facility
          await Promise.all([
            getVehicleSubcategory({
              setSubcategoryNames: setSubcategoryVehicle,
              businessId: firstFacility.id,
            }),
            getVehicleFuelSubcategory({
              setSubcategoryNames: setSubcategoryFuelVehicle,
              businessId: firstFacility.id,
            }),
            getOtherVehicleSubcategory({
              setSubcategoryNames: setSubcategoryOtherVehicle,
              businessId: firstFacility.id,
            }),
            getIngoingTransportSubcategory({
              businessId: firstFacility.id,
              setSubcategoryNames: setSubcategoryIngoingTransport,
            }),
            getOutgoingTransportSubcategory({
              businessId: firstFacility.id,
              setSubcategoryNames: setSubcategoryOutgoingTransport,
            }),
            getCustomSubcategory({
              businessId: firstFacility.id,
              setSubcategoryNames: setSubcategoryCustomInventory,
            }),
            getTravelSubcategory({
              businessId: firstFacility.id,
              setSubcategoryNames: setSubcategoryTravel,
            }),
            getWasteSubcategory({
              businessId: firstFacility.id,
              setSubcategoryNames: setSubcategoryWaste,
            }),
          ]);

          // Update parent row data with first facility
          const initialParentRowData = groupedData.reduce((acc, row) => {
            acc[row.account_number] = {
              facility: row.facility ? row.facility : firstFacility.value,
              facility_id: row.facility_id ? row.facility_id : firstFacility.id,
            };
            return acc;
          }, {});
          setParentRowData(initialParentRowData);

          // If there's a first category, set it and fetch its subcategories
          if (dropdownCategory.length > 0) {
            const firstCategory = dropdownCategory[0].value;
            const updatedMainDataWithCategory = updatedMainData.map((item) => ({
              ...item,
              category: item.category.value
                ? item.category.value
                : firstCategory,
            }));
            setMainData(updatedMainDataWithCategory);

            // Fetch subcategories for the first category
            const subcategories = categoriesWithSubcategories[
              firstCategory
            ].map((subcategory, index) => ({
              id: index + 1,
              title: subcategory,
              name: subcategory,
              value: subcategory,
              label: subcategory,
            }));
            setDropdownSubcategory(subcategories);

            const firstSubcategory = subcategories[0].value;
            const updatedMainDataWithSubcategory = updatedMainData.map(
              (item) => ({
                ...item,
                subcategory: item.subcategory.value
                  ? item.subcategory.value
                  : firstSubcategory,
              })
            );
            setMainData(updatedMainDataWithSubcategory);

            const initialSubcategoryStates = {};
            groupedData.forEach((row) => {
              const rowSubcategory = row.subcategory;
              const validSubcategory =
                rowSubcategory &&
                subcategories.some((sub) => sub.value === rowSubcategory);

              initialSubcategoryStates[row.account_number] = {
                options: subcategories,
                selected: validSubcategory
                  ? rowSubcategory
                  : subcategories[0]?.value || "",
              };
            });

            mainData.forEach(async (dataItem) => {
              const rowSubcategory = dataItem?.subcategory?.value;

              // Fetch subcategories for this specific dataItem
              const subcategoriesByCategory = await Promise.all([
                getCustomSubcategories(
                  "Registered Vehicle",
                  dataItem?.facility_id
                ),
                getCustomSubcategories("Fuel Vehicle", dataItem?.facility_id),
                getCustomSubcategories("Other Vehicle", dataItem?.facility_id),
                getCustomSubcategories(
                  "Ingoing Transport",
                  dataItem?.facility_id
                ),
                getCustomSubcategories(
                  "Outgoing Transport",
                  dataItem?.facility_id
                ),
                getCustomSubcategories("Inventory", dataItem?.facility_id),
                getCustomSubcategories(
                  "Business Travel and Hotel Stays",
                  dataItem?.facility_id
                ),
                getCustomSubcategories(
                  "Surplus Resources",
                  dataItem?.facility_id
                ),
              ]);

              // Create a mapping of categories to their subcategories
              const subcategoryMap = {
                "Registered Vehicle": subcategoriesByCategory[0],
                "Fuel Vehicle": subcategoriesByCategory[1],
                "Other Vehicle": subcategoriesByCategory[2],
                "Ingoing Transport": subcategoriesByCategory[3],
                "Outgoing Transport": subcategoriesByCategory[4],
                Inventory: subcategoriesByCategory[5],
                "Business Travel and Hotel Stays": subcategoriesByCategory[6],
                "Surplus Resources": subcategoriesByCategory[7],
              };

              // Update global subcategory states (if needed)
              setSubcategoryVehicle(
                subcategoryMap["Registered Vehicle"].map((sub) => sub.value)
              );
              setSubcategoryFuelVehicle(
                subcategoryMap["Fuel Vehicle"].map((sub) => sub.value)
              );
              setSubcategoryOtherVehicle(
                subcategoryMap["Other Vehicle"].map((sub) => sub.value)
              );
              setSubcategoryIngoingTransport(
                subcategoryMap["Ingoing Transport"].map((sub) => sub.value)
              );
              setSubcategoryOutgoingTransport(
                subcategoryMap["Outgoing Transport"].map((sub) => sub.value)
              );
              setSubcategoryCustomInventory(
                subcategoryMap["Inventory"].map((sub) => sub.value)
              );
              setSubcategoryTravel(
                subcategoryMap["Business Travel and Hotel Stays"].map(
                  (sub) => sub.value
                )
              );
              setSubcategoryWaste(
                subcategoryMap["Surplus Resources"].map((sub) => sub.value)
              );

              // Get options for this specific dataItem's category
              const individualOptions =
                subcategoryMap[dataItem?.category?.value] ||
                categoriesWithSubcategories[dataItem?.category?.value].map(
                  (subcategory, index) => ({
                    id: index + 1,
                    title: subcategory,
                    name: subcategory,
                    value: subcategory,
                    label: subcategory,
                  })
                );

              const validSubcategory =
                rowSubcategory &&
                individualOptions.some((sub) => sub.value === rowSubcategory);

              initialSubcategoryStates[dataItem.id] = {
                options: validSubcategory ? individualOptions : subcategories,
                selected: validSubcategory
                  ? rowSubcategory
                  : subcategories[0]?.value || "",
              };
            });
            setSubcategoryStates(initialSubcategoryStates);

            // Fetch emission factor options
            try {
              const emissionOptions =
                (await getDropdownOptions(
                  firstCategory,
                  initialSubcategoryStates[groupedData[0].account_number]
                    .selected,
                  businessId,
                  t
                )) || [];

              setDropdownEmission(emissionOptions);

              // If there are emission options, set the first one
              if (emissionOptions.length > 0) {
                const updatedMainDataWithEmission =
                  updatedMainDataWithCategory.map((item) => ({
                    ...item,
                    emission_factor: item.emission_factor.value
                      ? item.emission_factor.value
                      : emissionOptions[0].value,
                    typedb: item.typedb
                      ? item.typedb
                      : emissionOptions[0].typeDB || null,
                  }));
                setMainData(updatedMainDataWithEmission);

                // Set initial emission factor states for all rows
                const initialEmissionFactorStates = {};
                groupedData.forEach((row) => {
                  const rowEmissionFactor = row.emission_factor;
                  const validEmissionFactor =
                    rowEmissionFactor &&
                    emissionOptions.some(
                      (opt) => opt.value === rowEmissionFactor
                    );

                  initialEmissionFactorStates[row.account_number] = {
                    options: emissionOptions,
                    selected: validEmissionFactor
                      ? rowEmissionFactor
                      : emissionOptions[0]?.value || "",
                  };
                });

                mainData.forEach(async (dataItem) => {
                  const rowEmissionFactor = dataItem.emission_factor.value;
                  const individualOptions =
                    (await getDropdownOptions(
                      dataItem?.category?.value,
                      dataItem?.subcategory?.value,
                      businessId,
                      t
                    )) || [];
                  const validEmissionFactor =
                    rowEmissionFactor &&
                    individualOptions.some(
                      (opt) => opt.value === rowEmissionFactor
                    );

                  initialEmissionFactorStates[dataItem.id] = {
                    options: validEmissionFactor
                      ? individualOptions
                      : emissionOptions,
                    selected: validEmissionFactor
                      ? rowEmissionFactor
                      : emissionOptions[0]?.value || "",
                  };
                });
                setEmissionFactorStates(initialEmissionFactorStates);
              } else {
                // If no emission options, set empty states
                const initialEmissionFactorStates = {};
                groupedData.forEach((row) => {
                  initialEmissionFactorStates[row.account_number] = {
                    options: [],
                    selected: "",
                  };
                });
                setEmissionFactorStates(initialEmissionFactorStates);
              }
            } catch (error) {
              console.error("Error fetching emission options:", error);
              // Set empty emission factor states in case of error
              const initialEmissionFactorStates = {};
              groupedData.forEach((row) => {
                initialEmissionFactorStates[row.account_number] = {
                  options: [],
                  selected: "",
                };
              });
              setEmissionFactorStates(initialEmissionFactorStates);
            }
          }
        }
      }
    };

    populateInitialDropdowns();
  }, [
    mainData,
    dropdownFacility,
    dropdownCategory,
    categoriesWithSubcategories,
    businessId,
    facilityId,
    groupedData,
    t,
    dropdownSubcategory,
  ]);

  // Handling dropdown changes
  const handleDropdownChange = useCallback(
    async (rowId, field, value, isParentRow, item) => {
      // Create a copy of main data to modify
      const updatedMainData = mainData.map((item) => {
        const shouldUpdate =
          isParentRow === true
            ? item.account_number === rowId
            : item.id === rowId;

        if (shouldUpdate) {
          return {
            ...item,
            [field.replace(".value", "")]: value,
          };
        }
        return item;
      });

      // Update parent row data for independent tracking
      if (isParentRow) {
        setParentRowData((prev) => ({
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            [field.replace(".value", "")]: value,
          },
        }));
      }

      // Force state update
      setMainData([...updatedMainData]);

      // Facility change logic
      if (field === "facility.value") {
        const foundFacility = dropdownFacility.find(
          (facility) => facility.value === value
        );

        if (foundFacility && foundFacility.id) {
          setFacilityId(foundFacility.id);

          const updatedMainDataWithFacility = mainData.map((item) => {
            const shouldUpdate =
              isParentRow === true
                ? item.account_number === rowId
                : item.id === rowId;

            if (shouldUpdate) {
              return {
                ...item,
                facility: foundFacility.value,
                facility_id: foundFacility.id,
              };
            }
            return item;
          });

          setMainData(updatedMainDataWithFacility);

          // Fetch all facility-specific subcategories
          const subcategoryPromises = [
            getVehicleSubcategory({
              setSubcategoryNames: setSubcategoryVehicle,
              businessId: foundFacility.id,
            }),
            getVehicleFuelSubcategory({
              setSubcategoryNames: setSubcategoryFuelVehicle,
              businessId: foundFacility.id,
            }),
            getOtherVehicleSubcategory({
              setSubcategoryNames: setSubcategoryOtherVehicle,
              businessId: foundFacility.id,
            }),
            getIngoingTransportSubcategory({
              businessId: foundFacility.id,
              setSubcategoryNames: setSubcategoryIngoingTransport,
            }),
            getOutgoingTransportSubcategory({
              businessId: foundFacility.id,
              setSubcategoryNames: setSubcategoryOutgoingTransport,
            }),
            getCustomSubcategory({
              businessId: foundFacility.id,
              setSubcategoryNames: setSubcategoryCustomInventory,
            }),
            getTravelSubcategory({
              businessId: foundFacility.id,
              setSubcategoryNames: setSubcategoryTravel,
            }),
            getWasteSubcategory({
              businessId: foundFacility.id,
              setSubcategoryNames: setSubcategoryWaste,
            }),
          ];

          await Promise.all(subcategoryPromises);
        }
      }

      // Category change logic
      if (field === "category.value") {
        // Prepare facility-specific subcategories for this category
        const baseSubcategories = categoriesWithSubcategories[value] || [];

        // Add facility-specific subcategories if available
        const facilitySubcategories = {
          "Registered Vehicle": subcategoryVehicle,
          "Fuel Vehicle": subcategoryFuelVehicle,
          "Other Vehicle": subcategoryOtherVehicle,
          "Ingoing Transport": subcategoryIngoingTransport,
          "Outgoing Transport": subcategoryOutgoingTransport,
          "Business Travel and Hotel Stays": subcategoryTravel,
          "Surplus Resources": subcategoryWaste,
          Inventory: [
            ...baseSubcategories,
            ...(subcategoryCustomInventory || []),
          ],
        };

        // Combine and remove duplicates
        const combinedSubcategories = [
          ...new Set([
            ...(facilitySubcategories[value] || []),
            ...baseSubcategories,
          ]),
        ];

        const subcategories = combinedSubcategories.map(
          (subcategory, index) => ({
            id: index + 1,
            title: subcategory,
            name: subcategory,
            value: subcategory,
            label: subcategory,
          })
        );

        // When changing category for parent row, update all its children
        if (isParentRow) {
          try {
            const firstSubcategory =
              subcategories.length > 0 ? subcategories[0].value : null;
            const emissionOptions =
              (await getDropdownOptions(
                value,
                firstSubcategory,
                businessId,
                t
              )) || [];

            // Remove duplicates from emission options
            const uniqueEmissionOptions = [
              ...new Set(emissionOptions.map((option) => option.value)),
            ].map((value) =>
              emissionOptions.find((option) => option.value === value)
            );

            // Update states specifically for this parent row and its children
            const updatedSubcategoryStates = {
              [rowId]: {
                options: subcategories,
                selected:
                  subcategories.length > 0 ? subcategories[0].value : "",
              },
            };

            const updatedEmissionFactorStates = {
              [rowId]: {
                options: uniqueEmissionOptions,
                selected:
                  uniqueEmissionOptions.length > 0
                    ? uniqueEmissionOptions[0].value
                    : "",
              },
            };

            // Set the updated states only for this specific parent row
            setSubcategoryStates((prev) => ({
              ...prev,
              ...updatedSubcategoryStates,
            }));

            setEmissionFactorStates((prev) => ({
              ...prev,
              ...updatedEmissionFactorStates,
            }));

            const updatedMainDataWithCategory = updatedMainData.map((item) => {
              if (item.account_number === rowId) {
                return {
                  ...item,
                  category: value,
                  subcategory:
                    subcategories.length > 0 ? subcategories[0].value : "",
                  emission_factor:
                    uniqueEmissionOptions.length > 0
                      ? uniqueEmissionOptions[0].value
                      : "",
                  typedb:
                    uniqueEmissionOptions.length > 0
                      ? uniqueEmissionOptions[0].typeDB
                      : null,
                };
              }
              return item;
            });

            // Update child rows
            const childRows = updatedMainDataWithCategory.filter(
              (item) => item.account_number === rowId
            );

            childRows.forEach((childRow) => {
              updatedSubcategoryStates[childRow.id] = {
                options: subcategories,
                selected:
                  subcategories.length > 0 ? subcategories[0].value : "",
              };

              updatedEmissionFactorStates[childRow.id] = {
                options: uniqueEmissionOptions,
                selected:
                  uniqueEmissionOptions.length > 0
                    ? uniqueEmissionOptions[0].value
                    : "",
              };
            });

            setSubcategoryStates((prev) => ({
              ...prev,
              ...updatedSubcategoryStates,
            }));

            setEmissionFactorStates((prev) => ({
              ...prev,
              ...updatedEmissionFactorStates,
            }));

            setMainData(updatedMainDataWithCategory);
          } catch (error) {
            console.error("Error fetching emission options:", error);
          }
        } else {
          // For individual child row, only update its own states
          try {
            const emissionOptions =
              (await getDropdownOptions(
                value,
                subcategories[0]?.value,
                businessId,
                t
              )) || [];

            // Remove duplicates from emission options
            const uniqueEmissionOptions = [
              ...new Set(emissionOptions.map((option) => option.value)),
            ].map((value) =>
              emissionOptions.find((option) => option.value === value)
            );

            // Update states only for this specific child row
            const updatedSubcategoryStates = {
              [rowId]: {
                options: subcategories,
                selected: subcategories[0]?.value || "",
              },
            };

            const updatedEmissionFactorStates = {
              [rowId]: {
                options: uniqueEmissionOptions,
                selected: uniqueEmissionOptions[0]?.value || "",
              },
            };

            setSubcategoryStates((prev) => ({
              ...prev,
              ...updatedSubcategoryStates,
            }));

            setEmissionFactorStates((prev) => ({
              ...prev,
              ...updatedEmissionFactorStates,
            }));

            // Update only this specific child row's data
            const updatedData = mainData.map((item) => {
              if (item.id === rowId) {
                return {
                  ...item,
                  category: value,
                  subcategory: subcategories[0]?.value || "",
                  emission_factor: uniqueEmissionOptions[0]?.value || "",
                  typedb: uniqueEmissionOptions[0]?.typeDB || null,
                };
              }
              return item;
            });

            setMainData(updatedData);
          } catch (error) {
            console.error("Error fetching emission options:", error);
          }
        }
      }

      // Subcategory change logic
      if (field === "subcategory.value") {
        try {
          const emissionOptions =
            (await getDropdownOptions(item?.category, value, businessId, t)) ||
            [];

          // Remove duplicates from emission options
          const uniqueEmissionOptions = [
            ...new Set(emissionOptions.map((option) => option.value)),
          ].map((value) =>
            emissionOptions.find((option) => option.value === value)
          );

          const subcategoryPromises = [
            getVehicleSubcategory({
              setSubcategoryNames: setSubcategoryVehicle,
              businessId: item.facility_id,
            }),
            getVehicleFuelSubcategory({
              setSubcategoryNames: setSubcategoryFuelVehicle,
              businessId: item.facility_id,
            }),
            getOtherVehicleSubcategory({
              setSubcategoryNames: setSubcategoryOtherVehicle,
              businessId: item.facility_id,
            }),
            getIngoingTransportSubcategory({
              businessId: item.facility_id,
              setSubcategoryNames: setSubcategoryIngoingTransport,
            }),
            getOutgoingTransportSubcategory({
              businessId: item.facility_id,
              setSubcategoryNames: setSubcategoryOutgoingTransport,
            }),
            getCustomSubcategory({
              businessId: item.facility_id,
              setSubcategoryNames: setSubcategoryCustomInventory,
            }),
            getTravelSubcategory({
              businessId: item.facility_id,
              setSubcategoryNames: setSubcategoryTravel,
            }),
            getWasteSubcategory({
              businessId: item.facility_id,
              setSubcategoryNames: setSubcategoryWaste,
            }),
          ];

          await Promise.all(subcategoryPromises);

          // Update states only for this specific row
          const updatedSubcategoryStates = {
            [rowId]: {
              ...(subcategoryStates[rowId] || {}),
              selected: value,
            },
          };

          const updatedEmissionFactorStates = {
            [rowId]: {
              options: uniqueEmissionOptions,
              selected: uniqueEmissionOptions[0]?.value || "",
            },
          };

          setSubcategoryStates((prev) => ({
            ...prev,
            ...updatedSubcategoryStates,
          }));

          setEmissionFactorStates((prev) => ({
            ...prev,
            ...updatedEmissionFactorStates,
          }));

          // Update only this specific row's data
          const updatedData = mainData.map((item) => {
            if (item.id === rowId) {
              return {
                ...item,
                subcategory: value,
                emission_factor: uniqueEmissionOptions[0]?.value || "",
                typedb: uniqueEmissionOptions[0]?.typeDB || null,
              };
            }
            return item;
          });

          if (isParentRow) {
            const childRows = updatedData.filter(
              (item) => item.account_number === rowId
            );

            childRows.forEach((childRow) => {
              updatedSubcategoryStates[childRow.id] = {
                ...(subcategoryStates[childRow.id] || {}),
                selected: value,
              };

              updatedEmissionFactorStates[childRow.id] = {
                options: uniqueEmissionOptions,
                selected: uniqueEmissionOptions[0]?.value || "",
              };
            });

            setSubcategoryStates((prev) => ({
              ...prev,
              ...updatedSubcategoryStates,
            }));

            setEmissionFactorStates((prev) => ({
              ...prev,
              ...updatedEmissionFactorStates,
            }));
          }

          const childRows = updatedData.filter(
            (item) => item.account_number === rowId
          );

          const updatedChildData = updatedData.map((item) => {
            const childRow = childRows.find((child) => child.id === item.id);
            if (childRow) {
              return {
                ...item,
                subcategory: value,
                emission_factor: uniqueEmissionOptions[0]?.value || "",
                typedb: uniqueEmissionOptions[0]?.typeDB || null,
              };
            }
            return item;
          });

          setMainData(updatedChildData);
        } catch (error) {
          console.error("Error fetching emission options:", error);
        }
      }

      if (field === "emission_factor.value" && isParentRow) {
        // Find all child rows for this parent
        const childRows = updatedMainData.filter(
          (item) => item.account_number === rowId
        );

        // Update emission factor for all child rows
        const updatedChildData = updatedMainData.map((item) => {
          if (
            item.account_number === rowId &&
            item.id !== rowId // Exclude the parent row itself
          ) {
            return {
              ...item,
              emission_factor: value,
            };
          }
          return item;
        });

        // Update emission factor states for all rows in this group
        const updatedEmissionFactorStates = {};
        const currentEmissionOptions =
          emissionFactorStates[rowId]?.options || dropdownEmission;

        // Update parent row emission factor state
        updatedEmissionFactorStates[rowId] = {
          options: currentEmissionOptions,
          selected: value,
        };

        // Update child rows emission factor states
        childRows.forEach((childRow) => {
          updatedEmissionFactorStates[childRow.id] = {
            options: currentEmissionOptions,
            selected: value,
          };
        });

        // Set the updated states and data
        setEmissionFactorStates((prev) => ({
          ...prev,
          ...updatedEmissionFactorStates,
        }));

        // Update main data with new emission factors
        setMainData(updatedChildData);
      }

      if (field === "emission_factor.value" && !isParentRow) {
        // Update emission factor state for this specific child row
        const updatedEmissionFactorStates = {
          [rowId]: {
            ...(emissionFactorStates[rowId] || {}),
            selected: value,
          },
        };

        setEmissionFactorStates((prev) => ({
          ...prev,
          ...updatedEmissionFactorStates,
        }));

        // Update only this specific child row's data
        const updatedData = mainData.map((item) => {
          if (item.id === rowId) {
            const foundEmission = dropdownEmission.find(
              (emission) => emission.value === value
            );
            return {
              ...item,
              emission_factor: value,
              typedb: foundEmission.typeDB || null,
            };
          }
          return item;
        });

        setMainData(updatedData);
      }

      return;
    },
    [
      mainData,
      dropdownFacility,
      categoriesWithSubcategories,
      businessId,
      t,
      subcategoryStates,
      subcategoryVehicle,
      subcategoryFuelVehicle,
      subcategoryOtherVehicle,
      subcategoryIngoingTransport,
      subcategoryOutgoingTransport,
      subcategoryCustomInventory,
      subcategoryTravel,
      subcategoryWaste,
      dropdownEmission,
      emissionFactorStates,
    ]
  );

  useEffect(() => {
    if (subcategoryVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Registered Vehicle": subcategoryVehicle,
      }));
    }
    if (subcategoryFuelVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Fuel Vehicle": subcategoryFuelVehicle,
      }));
    }
    if (subcategoryOtherVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Other Vehicle": subcategoryOtherVehicle,
      }));
    }
    if (subcategoryIngoingTransport.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Ingoing Transport": subcategoryIngoingTransport,
      }));
    }
    if (subcategoryOutgoingTransport.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Outgoing Transport": subcategoryOutgoingTransport,
      }));
    }
    if (subcategoryCustomInventory.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        Inventory: [...(prev.Inventory || []), ...subcategoryCustomInventory],
      }));
    }
    if (subcategoryTravel.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Business Travel and Hotel Stays": subcategoryTravel,
      }));
    }
    if (subcategoryWaste.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Surplus Resources": [
          ...(prev["Surplus Resources"] || []),
          ...subcategoryWaste,
        ],
      }));
    }
  }, [
    subcategoryVehicle,
    subcategoryFuelVehicle,
    subcategoryOtherVehicle,
    subcategoryIngoingTransport,
    subcategoryOutgoingTransport,
    subcategoryCustomInventory,
    subcategoryTravel,
    subcategoryWaste,
  ]);
  // Update selected rows effect
  useEffect(() => {
    if (!isView) {
      const selectedData = mainData
        .filter((item) => selectedRows[item.id])
        .map((item) => ({
          ...item,
          facility_id: facilityId,
          subcategory:
            item.subcategory && Object.keys(item.subcategory).length > 0
              ? item.subcategory
              : subcategoryStates[item.account_number]?.selected ||
                parentRowData[item.account_number]?.subcategory ||
                null,
        }));
      setSelectedRowsInStep2(selectedData);
    }
  }, [
    selectedRows,
    mainData,
    setSelectedRowsInStep2,
    facilityId,
    parentRowData,
    subcategoryStates,
    isView,
  ]);

  // Row expansion and selection methods
  const handleExpandRow = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  }, []);

  const handleSelectChildRow = useCallback(
    (parentId, childId) => {
      setSelectedRows((prev) => {
        const newSelectedRows = { ...prev };
        newSelectedRows[childId] = !prev[childId];

        const childRows = mainData.filter(
          (item) => item.account_number === parentId
        );

        const allChildrenSelected = childRows.every(
          (child) => newSelectedRows[child.id]
        );

        newSelectedRows[parentId] = allChildrenSelected;

        return newSelectedRows;
      });
    },
    [mainData]
  );

  const renderParentRow = useCallback(
    (row) => {
      const isExpanded = expandedRows[row.id];
      const isSelected = selectedRows[row.id];

      const rowSubcategoryState = subcategoryStates[row.account_number] || {
        options: dropdownSubcategory,
        selected: row.subcategory || "",
      };

      const rowEmissionFactorState = emissionFactorStates[
        row.account_number
      ] || {
        options: dropdownEmission,
        selected: row.emission_factor || "",
      };

      const handleParentCheckboxChange = () => {
        const parentRowSelected = !isSelected;

        setSelectedRows((prev) => ({
          ...prev,
          [row.id]: parentRowSelected,
        }));

        const childRows = mainData.filter(
          (item) => item.account_number === row.account_number
        );

        const updatedSelectedRows = childRows.reduce((acc, childRow) => {
          acc[childRow.id] = parentRowSelected;
          return acc;
        }, {});

        setSelectedRows((prev) => ({
          ...prev,
          ...updatedSelectedRows,
        }));
      };

      return (
        <>
          <TableRow className="erp-table-rows">
            <TableCell className="erp-table-cells">
              {row.account_name}
            </TableCell>
            <TableCell className="erp-table-cells">
              {row.account_number}
            </TableCell>
            <TableCell className="erp-table-cells">
              <div
                style={{ display: "flex", alignItems: "center", gap: "4.80px" }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleExpandRow(row.id)}
                  sx={{
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginRight: "4.80px",
                  }}
                >
                  {isExpanded ? (
                    <img src={upArrow} alt="Arrow up" width={16} height={16} />
                  ) : (
                    <img
                      src={downArrow}
                      alt="Arrow down"
                      width={16}
                      height={16}
                    />
                  )}
                </IconButton>
                {!isView && (
                  <Checkbox
                    checked={isSelected}
                    onChange={handleParentCheckboxChange}
                    sx={{ paddingLeft: 0, paddingRight: 0 }}
                    icon={
                      <img
                        src={unselectedCheck}
                        alt="unchecked"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                    checkedIcon={
                      <img
                        src={selectedCheck}
                        alt="checked"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                  />
                )}
              </div>
            </TableCell>

            <TableCell className="erp-table-cells">
              <select
                className="erp-table-dropdown"
                style={{ width: "100%" }}
                value={row.facility || ""}
                onChange={(e) => {
                  handleDropdownChange(
                    row.account_number,
                    "facility.value",
                    e.target.value,
                    true
                  );
                }}
              >
                {dropdownFacility &&
                  dropdownFacility.map((option) => (
                    <option
                      key={option.id}
                      value={option.value}
                      label={option.value}
                    >
                      {option.value}
                    </option>
                  ))}
              </select>
            </TableCell>
            <TableCell className="erp-table-cells">
              <select
                className="erp-table-dropdown"
                style={{ width: "100%" }}
                value={row.category || ""}
                onChange={(e) =>
                  handleDropdownChange(
                    row.account_number,
                    "category.value",
                    e.target.value,
                    true
                  )
                }
              >
                {dropdownCategory &&
                  dropdownCategory.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      label={option.value}
                    >
                      {option.value}
                    </option>
                  ))}
              </select>
            </TableCell>
            <TableCell className="erp-table-cells">
              <select
                className="erp-table-dropdown"
                style={{ width: "100px" }}
                value={row.subcategory}
                onChange={(e) =>
                  handleDropdownChange(
                    row.account_number,
                    "subcategory.value",
                    e.target.value,
                    true,
                    parentRowData[row.account_number]
                  )
                }
              >
                {rowSubcategoryState.options &&
                  Array.isArray(rowSubcategoryState.options) &&
                  rowSubcategoryState.options.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      label={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
              </select>
            </TableCell>
            <TableCell className="erp-table-cells">
              <select
                className="erp-table-dropdown"
                style={{ width: "100px" }}
                value={row.emission_factor}
                onChange={(e) =>
                  handleDropdownChange(
                    row.account_number,
                    "emission_factor.value",
                    e.target.value,
                    true
                  )
                }
              >
                {rowEmissionFactorState.options &&
                  Array.isArray(rowEmissionFactorState.options) &&
                  rowEmissionFactorState.options.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      label={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
              </select>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="header-text-erp-table child">
                        Date
                      </TableCell>
                      <TableCell className="header-text-erp-table child">
                        Text
                      </TableCell>
                      <TableCell className="header-text-erp-table child">
                        Amount
                      </TableCell>
                      <TableCell className="header-text-erp-table child">
                        Entry No.
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mainData
                      .filter(
                        (item) => item.account_number === row.account_number
                      )
                      .map((item, index) => {
                        const itemSubcategoryState =
                          subcategoryStates[item.id] || rowSubcategoryState;
                        const itemEmissionFactorState =
                          emissionFactorStates[item.id] ||
                          rowEmissionFactorState;
                        const isChildSelected = selectedRows[item.id];
                        return (
                          <TableRow
                            className={index % 2 ? "" : "erp-table-child-row"}
                            key={item.id}
                          >
                            <TableCell>{item.invoice_date}</TableCell>
                            <TableCell>{item.text}</TableCell>
                            <TableCell>{item.amount}</TableCell>
                            <TableCell>{item.entry_number}</TableCell>
                            {!isView && (
                              <TableCell>
                                <Checkbox
                                  checked={isChildSelected}
                                  onChange={() =>
                                    handleSelectChildRow(row.id, item.id)
                                  }
                                  icon={
                                    <img
                                      src={unselectedCheck}
                                      alt="unchecked"
                                      style={{ width: 16, height: 16 }}
                                    />
                                  }
                                  checkedIcon={
                                    <img
                                      src={selectedCheck}
                                      alt="checked"
                                      style={{ width: 16, height: 16 }}
                                    />
                                  }
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <select
                                className="erp-table-dropdown erp-table-dropdown-child"
                                style={{ width: "100px" }}
                                value={item.facility || row.facility || ""}
                                onChange={(e) =>
                                  handleDropdownChange(
                                    item.id,
                                    "facility.value",
                                    e.target.value,
                                    false
                                  )
                                }
                              >
                                {dropdownFacility.map((option) => (
                                  <option
                                    key={option.id}
                                    value={option.value}
                                    label={option.label}
                                  >
                                    {option.value}
                                  </option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell>
                              <select
                                className="erp-table-dropdown erp-table-dropdown-child"
                                style={{ width: "100px" }}
                                value={item.category || row.category || ""}
                                onChange={(e) =>
                                  handleDropdownChange(
                                    item.id,
                                    "category.value",
                                    e.target.value,
                                    false
                                  )
                                }
                              >
                                {dropdownCategory.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                    label={option.label}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell>
                              <select
                                className="erp-table-dropdown erp-table-dropdown-child"
                                style={{ width: "100px" }}
                                value={itemSubcategoryState.selected}
                                onChange={(e) =>
                                  handleDropdownChange(
                                    item.id,
                                    "subcategory.value",
                                    e.target.value,
                                    false,
                                    item
                                  )
                                }
                              >
                                {itemSubcategoryState.options &&
                                  Array.isArray(itemSubcategoryState.options) &&
                                  itemSubcategoryState.options.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                      label={option.label}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                              </select>
                            </TableCell>
                            <TableCell>
                              <select
                                className="erp-table-dropdown erp-table-dropdown-child"
                                style={{ width: "100px" }}
                                value={itemEmissionFactorState.selected}
                                onChange={(e) =>
                                  handleDropdownChange(
                                    item.id,
                                    "emission_factor.value",
                                    e.target.value,
                                    false
                                  )
                                }
                              >
                                {itemEmissionFactorState.options &&
                                  Array.isArray(
                                    itemEmissionFactorState.options
                                  ) &&
                                  itemEmissionFactorState.options.map(
                                    (option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                        label={option.label}
                                      >
                                        {option.value}
                                      </option>
                                    )
                                  )}
                              </select>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        </>
      );
    },
    [
      expandedRows,
      selectedRows,
      handleExpandRow,
      handleDropdownChange,
      dropdownFacility,
      dropdownCategory,
      dropdownSubcategory,
      dropdownEmission,
      mainData,
      emissionFactorStates,
      handleSelectChildRow,
      subcategoryStates,
      parentRowData,
      isView,
    ]
  );

  const handleSelectAll = () => {
    setSelectedRows((prev) => {
      // If all rows are currently selected, unselect everything
      if (selectedRowsInStep2.length === mainData.length) {
        return {};
      }

      // Otherwise, select all rows
      const newSelectedRows = {};
      mainData.forEach((row) => {
        newSelectedRows[row.id] = true;
      });

      return newSelectedRows;
    });
  };

  const handleSort = (column) => {
    // Toggle sorting direction
    const newSortDirection =
      sortColumn === column && sortDirection === "asc" ? "desc" : "asc";

    // Update sort parameters
    setSortColumn(column);
    setSortDirection(newSortDirection);
  };

  return (
    <div className="erp-table-bg">
      <div className="table-actions">
        <div className="sorting-erp" style={{ cursor: "pointer" }}>
          <img
            src={sortingIcon}
            alt="Sorting Icon"
            width={18}
            height={18}
            onClick={() => handleSort("account_number")}
          />
        </div>
        {!isView && (
          <div className="selected-number">
            <p
              style={{
                color: "#0C376A",
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: 0,
              }}
            >
              {selectedRowsInStep2.length === mainData.length
                ? "All Selected"
                : selectedRowsInStep2.length === 0
                ? "None Selected"
                : "Multiple Selected"}
            </p>
          </div>
        )}
        {!isView && (
          <div className="bulk-actions" style={{ cursor: "pointer" }}>
            <button onClick={handleSelectAll} className="btn btn-primary">
              {selectedRowsInStep2.length === mainData.length ? (
                <img
                  src={selectedCheck}
                  alt="All selected"
                  width={16}
                  height={16}
                />
              ) : (
                <img
                  src={selectAllUnselected}
                  alt="Not selected"
                  width={16}
                  height={16}
                />
              )}
            </button>
          </div>
        )}
      </div>
      <div className="erp-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={4}
                className="text-center header-text-erp-table"
              >
                General & Admin
              </TableCell>
              <TableCell className="header-text-erp-table">Facility</TableCell>
              <TableCell
                className="header-text-erp-table"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                }}
              >
                <p style={{ marginBottom: 0 }}>Category</p>
                <img
                  src={infoIcon}
                  alt="Info Icon"
                  width={20}
                  height={20}
                  onClick={() => setOpenModal(true)}
                  style={{ cursor: "pointer", marginLeft: "8px" }}
                />
              </TableCell>
              <TableCell className="header-text-erp-table">
                Subcategory
              </TableCell>
              <TableCell className="header-text-erp-table">
                Product/material/equipment
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData.map((row) => renderParentRow(row))}
          </TableBody>
        </Table>
      </div>
      {openModal && <AllocateModal onClose={() => setOpenModal(false)} />}
    </div>
  );
};

export default React.memo(Step2Table);
